@let scan = scan$ | async;
@let rows = scanResults();
@let filtersDisabled = !areFiltersEnabled();

<section class="scanner-details">
  <mon-page-toolbar
    titleTranslateKey="monitoring.scanner.details.title"
    [backLink]="['/scanner']"
    [searchControl]="detailsFiltersForm.controls.keyword"
    [searchDisabled]="filtersDisabled"
    [selectedFiltersCount]="selectedFiltersCount"
  >
    <form
      toolbarFilters
      class="scanner-details__filters"
      [class.scanner-details__filters--disabled]="filtersDisabled"
      [formGroup]="detailsFiltersForm"
    >
      <div class="scanner-details__filters-wrapper">
        <lib-multiselect
          class="scanner-details__filter"
          titleTranslationKey="monitoring.scanner.details.filters.result"
          chosenTranslationKey="monitoring.scanner.details.filters.results-chosen"
          clearAllTranslationKey="monitoring.clear"
          [options]="(statusOptions$ | async) ?? []"
          formControlName="statusIds"
          [disabled]="filtersDisabled"
        />

        @if (canReadEquipmentModels()) {
          <mon-equipment-model-selector
            class="scanner-details__filter"
            [controlEntity]="detailsFiltersForm.controls.modelIds"
            [disabled]="filtersDisabled"
          />
        }
      </div>

      @if (filtersDisabled) {
        <span class="scanner-details__filters-hint">
          {{ 'monitoring.scanner.details.filters.pending' | translate }}
        </span>
      }
    </form>

    @if (isFiltersActive) {
      <lib-button
        toolbarSelectedFilters
        class="chip chip--button scanner-details__clear"
        buttonStyle="danger"
        buttonSize="medium"
        buttonType="button"
        (click)="clearAllFilters()"
      >
        <span>{{ 'monitoring.clear-filters' | translate }}</span>
      </lib-button>
    }
  </mon-page-toolbar>

  @if (scan) {
    <section class="scanner-details__summary sticky">
      <div class="scanner-details__info-card">
        <div class="scanner-details__info-item">
          <span class="scanner-details__info-label">
            {{ 'monitoring.scanner.details.facility' | translate }}
          </span>
          <span class="scanner-details__info-value">
            {{ scan.facility.name || '-' }}
          </span>
        </div>

        @if (scan.containersCount > 0) {
          <button
            class="scanner-details__info-item scanner-details__info-item--interactive"
            type="button"
            (click)="openContainersModal(scan.containers)"
          >
            <span class="scanner-details__info-label">
              {{ 'monitoring.scanner.table.container' | translate }}
            </span>
            <span class="scanner-details__info-value scanner-details__info-value--with-icon">
              <lib-svg-icon
                icon="info"
                [size]="20"
              />
              {{ scan.containersCount }}
            </span>
          </button>
        } @else {
          <div class="scanner-details__info-item">
            <span class="scanner-details__info-label">
              {{ 'monitoring.scanner.table.container' | translate }}
            </span>
            <span class="scanner-details__info-value">
              {{ scan.containersCount }}
            </span>
          </div>
        }

        <div class="scanner-details__info-item">
          <span class="scanner-details__info-label">
            {{ 'monitoring.scanner.details.found' | translate }}
          </span>
          <span class="scanner-details__info-value">
            {{ detectedCounter.displayed() }}/{{ getTotalScanCount(scan) }}
          </span>
        </div>
      </div>

      <div
        class="scanner-details__progress-panel"
        [ngClass]="'scanner-details__progress-panel--' + progressPanelState(currentStatus())"
      >
        <div
          class="scanner-details__progress-card"
          role="progressbar"
          aria-valuemin="0"
          [attr.aria-valuemax]="totalScanned()"
          [attr.aria-valuenow]="scannedCounter.displayed()"
        >
          <div
            class="scanner-details__progress-fill"
            [style.width.%]="progressBarWidth()"
          ></div>

          <div class="scanner-details__progress-content">
            <span class="scanner-details__progress-title">
              {{ progressLabelKey(currentStatus()) | translate }}
            </span>
            <span class="scanner-details__progress-value">
              {{ scannedCounter.displayed() }}/{{ totalScanned() }} ({{ progressPercent() }}%)
            </span>
          </div>
        </div>

        @if (canCancel) {
          <lib-button
            class="scanner-details__summary-action scanner-details__summary-action--cancel"
            buttonSize="large"
            buttonStyle="danger"
            [disabled]="isCancelling()"
            [libLoader]="isCancelling()"
            (click)="cancelScan()"
          >
            {{ 'monitoring.scanner.details.cancel' | translate }}
          </lib-button>
        }

        @if (canApprove) {
          <lib-button
            class="scanner-details__summary-action scanner-details__summary-action--approve"
            buttonSize="large"
            buttonStyle="transparent-tertiary"
            [disabled]="isApproving()"
            [libLoader]="isApproving()"
            (click)="approveScanResult()"
          >
            {{ 'monitoring.scanner.details.approve' | translate }}
          </lib-button>
        }
      </div>
    </section>
  } @else {
    <section class="scanner-details__summary scanner-details__summary--skeleton">
      <div
        class="scanner-details__summary-skeleton scanner-details__summary-skeleton--info"
        [libSkeleton]="true"
      ></div>

      <div
        class="scanner-details__summary-skeleton scanner-details__summary-skeleton--progress"
        [libSkeleton]="true"
      ></div>
    </section>
  }
  <section class="page-content page-table scanner-details__table-section">
    <div class="scanner-details__table">
      <lib-table
        class="scanner-details__table-component"
        trCustomClass="scanner-details__table-row"
        [cellTemplates]="{
          containerName: containerNameTpl,
          ipAddress: ipAddressTpl,
          model: modelTpl,
          macAddress: macAddressTpl,
          result: resultTpl,
          detectedAtUtc: detectedAtTpl,
        }"
        [columns]="SCANNER_RESULTS_COLUMNS"
        [dataSource]="rows"
        [loaded]="scanResultsLoaded()"
        [loading]="scanResultsLoading()"
        [emptyState]="' '"
      />

      @if (rows.length === 0 && scanResultsLoaded() && !scanResultsLoading()) {
        <div class="scanner-details__empty-state">
          <lib-svg-icon
            icon="scanner"
            [size]="36"
          />
          <span>{{ emptyStateKey | translate }}</span>
        </div>
      }
    </div>

    @if (totalCount() > (searchRequest$.value.take ?? 100)) {
      <lib-pagination
        [totalCount]="totalCount()"
        [pageIndex]="currentPageIndex"
        [pageSize]="100"
        [loading]="scanResultsLoading()"
        (paginationChange)="onPageChange($event)"
      />
    }
  </section>

  <ng-template
    #containerNameTpl
    let-row="data"
  >
    @let data = asSearchScanResult(row);
    <div class="scanner-details__container-cell">
      <lib-svg-icon
        icon="info"
        [size]="16"
      />
      <span>{{ data.containerName || '-' }}</span>
    </div>
  </ng-template>

  <ng-template
    #ipAddressTpl
    let-row="data"
  >
    @let data = asSearchScanResult(row);
    <span class="scanner-details__cell-value">
      {{ data.ipAddress || '-' }}
    </span>
  </ng-template>

  <ng-template
    #modelTpl
    let-row="data"
  >
    @let data = asSearchScanResult(row);
    @let rawModel = getDeviceRawModel(data);

    <div class="scanner-details__device">
      @if (hasDeviceModel(data)) {
        <span class="scanner-details__device-title">
          {{ getDeviceName(data) }}
        </span>
      } @else {
        @if (canSelectModel() && requiresModelSelection(data)) {
          <button
            class="scanner-details__device-select"
            type="button"
            [title]="'monitoring.choose' | translate"
            (click)="openSelectModel(data)"
          >
            {{ 'monitoring.choose' | translate }}
          </button>
        } @else {
          <span class="scanner-details__device-title">
            {{ getDeviceName(data) }}
          </span>
        }

        @if (rawModel) {
          <span class="scanner-details__device-meta">
            {{ rawModel }}
          </span>
        }
      }
    </div>
  </ng-template>

  <ng-template
    #macAddressTpl
    let-row="data"
  >
    @let data = asSearchScanResult(row);
    {{ data.macAddress | macAddress }}
  </ng-template>

  <ng-template
    #resultTpl
    let-row="data"
  >
    @let data = asSearchScanResult(row);
    @let badge = getScanResultBadge(data);
    @let previousDeviceInfo = getResultPreviousDevicePopover(data);
    @let currentDeviceInfo = getResultCurrentDevicePopover(data);

    <div class="scanner-details__status-cell">
      <span
        class="scanner-details__badge scanner-details__result-badge"
        [ngClass]="'scanner-details__badge--' + badge.modifier"
      >
        {{ badge.key | translate }}
      </span>

      @if (shouldShowResultDevices(data)) {
        <div class="scanner-details__result-devices">
          <span class="scanner-details__result-device">
            @if (previousDeviceInfo) {
              <mon-hover-popover
                [template]="resultDevicePopoverTpl"
                [context]="{ $implicit: previousDeviceInfo }"
              >
                <span class="scanner-details__result-device-anchor">
                  <lib-svg-icon
                    icon="info"
                    [size]="14"
                  />
                  <span>{{ previousDeviceInfo.modelName }}</span>
                </span>
              </mon-hover-popover>
            } @else {
              <span>{{ getResultPreviousDeviceName(data) }}</span>
            }
          </span>

          <lib-svg-icon
            class="scanner-details__result-arrow"
            icon="arrow-right"
            [size]="14"
          />

          <span class="scanner-details__result-device">
            @if (currentDeviceInfo) {
              <mon-hover-popover
                [template]="resultDevicePopoverTpl"
                [context]="{ $implicit: currentDeviceInfo }"
              >
                <span class="scanner-details__result-device-anchor">
                  <lib-svg-icon
                    icon="info"
                    [size]="14"
                  />
                  <span>{{ currentDeviceInfo.modelName }}</span>
                </span>
              </mon-hover-popover>
            } @else {
              <span>{{ getResultCurrentDeviceName(data) }}</span>
            }
          </span>
        </div>
      }
    </div>
  </ng-template>

  <ng-template
    #resultDevicePopoverTpl
    let-device
  >
    <strong class="popover-title">
      {{ device.modelName }}
    </strong>

    <span class="popover-row">
      <span class="popover-label">
        {{ 'monitoring.scanner.details.device-popover.ip' | translate }}
      </span>
      <span class="popover-value">
        {{ device.ipAddress }}
      </span>
    </span>

    <span class="popover-row">
      <span class="popover-label">
        {{ 'monitoring.scanner.details.device-popover.mac' | translate }}
      </span>
      <span class="popover-value">
        {{ device.macAddress | macAddress }}
      </span>
    </span>

    <span class="popover-row">
      <span class="popover-label">
        {{ 'monitoring.scanner.details.device-popover.firmware' | translate }}
      </span>
      <span class="popover-value">
        {{ device.firmware }}
      </span>
    </span>

    <!-- Эталон показываем как «найдено на устройстве»: значение ещё проходит серверные
         проверки, и исполнение из него может не появиться. -->
    @if (device.referenceHashrate) {
      <span class="popover-row">
        <span class="popover-label">
          {{ 'monitoring.scanner.details.device-popover.reference' | translate }}
        </span>
        <span
          class="popover-value"
          [class.scanner-details__reference-mismatch]="device.referenceMismatch"
        >
          {{ device.referenceHashrate }}
        </span>
      </span>

      @if (device.referenceSource) {
        <span class="popover-row">
          <span class="popover-label">
            {{ 'monitoring.devices.sub-model.source' | translate }}
          </span>
          <span class="popover-value">{{ device.referenceSource }}</span>
        </span>
      }
    }
  </ng-template>

  <ng-template
    #detectedAtTpl
    let-row="data"
  >
    @let data = asSearchScanResult(row);
    @if (data.detectedAtUtc) {
      <div class="scanner-details__date">
        <span>{{ data.detectedAtUtc | date: 'yyyy.MM.dd' : 'UTC' }}</span>
        <span>{{ data.detectedAtUtc | date: 'HH:mm:ss' : 'UTC' }} (UTC)</span>
      </div>
    } @else {
      -
    }
  </ng-template>
</section>
